/* dns-custom-socks.js — Custom Sock Builder
   State machine + two-request Ajax add-to-cart (sock line first, fee lines second,
   rollback the sock line if the fee add fails). Pricing comes from the section's
   JSON config (built from live variants) — nothing hardcoded here. */

(function () {
  'use strict';

  const STORAGE_KEY = 'dns-custom-socks-v2';
  /* pre-split state ('v1': single qty, no color) — migrated on restore, removed after */
  const LEGACY_STORAGE_KEY = 'dns-custom-socks-v1';

  class DnsCustomSocks extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-dns-cs-config]');
      if (!configEl) return;
      this.cfg = JSON.parse(configEl.textContent);
      this.str = this.cfg.strings || {};
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.cfg.currency });

      this.els = {
        radios: Array.from(this.querySelectorAll('input[name="dns-cs-tier"]')),
        cards: Array.from(this.querySelectorAll('.dns-cs__card')),
        qty: this.querySelector('#dns-cs-qty'),
        qtyAdult: this.querySelector('#dns-cs-qty-adult'),
        qtyYouth: this.querySelector('#dns-cs-qty-youth'),
        qtyTotal: this.querySelector('[data-dns-cs-qty-total]'),
        colorRadios: Array.from(this.querySelectorAll('input[name="dns-cs-color"]')),
        colorOtherWrap: this.querySelector('[data-dns-cs-color-other]'),
        colorOther: this.querySelector('#dns-cs-color-other'),
        file: this.querySelector('#dns-cs-file'),
        fileHint: this.querySelector('[data-dns-cs-file-hint]'),
        fileList: this.querySelector('[data-dns-cs-file-list]'),
        notes: this.querySelector('#dns-cs-notes'),
        rows: Array.from(this.querySelectorAll('[data-dns-cs-row]')),
        summary: this.querySelector('[data-dns-cs-summary]'),
        contact: this.querySelector('[data-dns-cs-contact]'),
        pairsLabel: this.querySelector('[data-dns-cs-pairs-label]'),
        pairsTotal: this.querySelector('[data-dns-cs-pairs-total]'),
        setupLine: this.querySelector('[data-dns-cs-setup-line]'),
        setupTotal: this.querySelector('[data-dns-cs-setup-total]'),
        designLine: this.querySelector('[data-dns-cs-design-line]'),
        designLabel: this.querySelector('[data-dns-cs-design-label]'),
        designTotal: this.querySelector('[data-dns-cs-design-total]'),
        total: this.querySelector('[data-dns-cs-total]'),
        tierNote: this.querySelector('[data-dns-cs-tier-note]'),
        quoteQty: this.querySelector('[data-dns-cs-quote-qty]'),
        quoteLink: this.querySelector('[data-dns-cs-quote-link]'),
        atc: this.querySelector('[data-dns-cs-atc]'),
        status: this.querySelector('[data-dns-cs-status]'),
        step2: this.querySelector('[data-dns-cs-step2]'),
        step3: this.querySelector('[data-dns-cs-step3]'),
        step3Label: this.querySelector('[data-dns-cs-step3-label]'),
        artField: this.querySelector('[data-dns-cs-art-field]'),
        notesLabel: this.querySelector('[data-dns-cs-notes-label]')
      };

      /* split mode = the v2 builder markup with separate adult/youth fields; the v1
         section (dns-custom-socks.liquid) still has the single #dns-cs-qty and runs
         every legacy path below. state.qty is ALWAYS the combined total, so pricing,
         tiers and the quote path never need to know about the split. */
      this.split = !!(this.els.qtyAdult && this.els.qtyYouth);

      this.state = { tier: null, qty: this.cfg.limits.min, qtyAdult: this.cfg.limits.min, qtyYouth: 0, notes: '', color: null, colorOther: '' };
      this.restore();
      this.bind();
      this.render();
    }

    /* ---------- state ---------- */

    restore() {
      try {
        let saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!saved) {
          /* migrate pre-split state: its single qty becomes adult pairs */
          saved = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY));
          if (saved) {
            if (typeof saved.qty === 'number') saved.qtyAdult = saved.qty;
            saved.qtyYouth = 0;
            localStorage.removeItem(LEGACY_STORAGE_KEY);
          }
        }
        if (!saved) return;
        if (typeof saved.qty === 'number') this.state.qty = saved.qty;
        if (typeof saved.qtyAdult === 'number') this.state.qtyAdult = Math.max(0, saved.qtyAdult);
        if (typeof saved.qtyYouth === 'number') this.state.qtyYouth = Math.max(0, saved.qtyYouth);
        if (typeof saved.notes === 'string') this.state.notes = saved.notes;
        if (typeof saved.color === 'string') this.state.color = saved.color;
        if (typeof saved.colorOther === 'string') this.state.colorOther = saved.colorOther;
        if (saved.tier && this.els.radios.some((r) => r.value === saved.tier)) {
          this.state.tier = saved.tier;
        }
      } catch (e) { /* corrupt or unavailable storage — start fresh */ }

      if (this.split) {
        /* the split fields are the source of truth — never trust a stored total */
        this.state.qty = this.state.qtyAdult + this.state.qtyYouth;
        this.els.qtyAdult.value = this.state.qtyAdult;
        this.els.qtyYouth.value = this.state.qtyYouth;
      } else if (this.els.qty) {
        this.els.qty.value = this.state.qty;
      }
      this.els.notes.value = this.state.notes;
      if (this.state.tier) {
        const radio = this.els.radios.find((r) => r.value === this.state.tier);
        if (radio) radio.checked = true;
      }
      if (this.state.color) {
        const colorRadio = this.els.colorRadios.find((r) => r.value === this.state.color);
        if (colorRadio) {
          colorRadio.checked = true;
        } else {
          this.state.color = null; /* stale value (swatch list changed) — pick again */
        }
      }
      if (this.els.colorOther) this.els.colorOther.value = this.state.colorOther;
      if (this.els.colorOtherWrap) this.els.colorOtherWrap.hidden = this.state.color !== 'other';
    }

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (e) { /* storage unavailable — non-fatal */ }
    }

    bind() {
      this.els.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.state.tier = radio.value;
          this.save();
          this.render();
        });
      });

      if (this.split) {
        const syncQty = () => {
          const a = parseInt(this.els.qtyAdult.value, 10);
          const y = parseInt(this.els.qtyYouth.value, 10);
          this.state.qtyAdult = Math.max(0, Number.isFinite(a) ? a : 0);
          this.state.qtyYouth = Math.max(0, Number.isFinite(y) ? y : 0);
          this.state.qty = this.state.qtyAdult + this.state.qtyYouth;
          this.render();
        };
        /* no clamp-to-min on change: the 12-pair minimum is on the COMBINED total, so
           force-filling one field would corrupt an order the other field completes.
           The total line's error state and the ATC gate carry the minimum instead. */
        [this.els.qtyAdult, this.els.qtyYouth].forEach((input) => {
          input.addEventListener('input', syncQty);
          input.addEventListener('change', () => { syncQty(); this.save(); });
        });
      } else {
        this.els.qty.addEventListener('input', () => {
          const val = parseInt(this.els.qty.value, 10);
          this.state.qty = Number.isFinite(val) ? val : 0;
          this.render();
        });

        this.els.qty.addEventListener('change', () => {
          if (this.state.qty < this.cfg.limits.min) {
            this.state.qty = this.cfg.limits.min;
            this.els.qty.value = this.state.qty;
          }
          this.save();
          this.render();
        });
      }

      this.els.colorRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.state.color = radio.value;
          if (this.els.colorOtherWrap) this.els.colorOtherWrap.hidden = radio.value !== 'other';
          this.save();
          this.render();
        });
      });

      if (this.els.colorOther) {
        this.els.colorOther.addEventListener('change', () => {
          this.state.colorOther = this.els.colorOther.value;
          this.save();
        });
        /* the quote mailto carries the color too, so it has to keep up as they type */
        this.els.colorOther.addEventListener('input', () => {
          this.state.colorOther = this.els.colorOther.value;
          if (this.needsQuote(this.state.qty)) {
            this.renderQuote(Math.max(this.state.qty, this.cfg.limits.min));
          }
        });
      }

      this.els.notes.addEventListener('change', () => {
        this.state.notes = this.els.notes.value;
        this.save();
      });

      /* the quote mailto carries the design notes, so it has to keep up as they type */
      this.els.notes.addEventListener('input', () => {
        if (this.needsQuote(this.state.qty)) {
          this.renderQuote(Math.max(this.state.qty, this.cfg.limits.min));
        }
      });

      this.els.file.addEventListener('change', () => {
        this.setStatus('');
        const files = Array.from(this.els.file.files);
        const maxFiles = this.cfg.maxFiles || 1;
        /* reject the whole selection, not just the offenders — a silently-trimmed
           upload reads as "all my files made it" when they didn't */
        if (files.length > maxFiles) {
          this.setStatus((this.str.tooManyFiles || '').replace(/\{n\}/g, maxFiles), true);
          this.els.file.value = '';
        } else if (files.some((f) => f.size > this.cfg.maxFileMb * 1024 * 1024)) {
          this.setStatus(this.str.fileTooBig.replace('{mb}', this.cfg.maxFileMb), true);
          this.els.file.value = '';
        }
        this.renderFileList();
      });

      this.els.atc.addEventListener('click', () => this.addToCart());
    }

    /* ---------- pricing ---------- */

    tierForQty(qty) {
      return this.cfg.tiers.find((t) => qty >= t.min && qty <= t.max) || null;
    }

    /* Orders at or above the top pricing tier (cfg.quoteFrom, default 201) are quoted by
       a human instead of self-served, so anything over it — including over the max — swaps
       the judge's card for the email prompt. */
    needsQuote(qty) {
      const from = this.cfg.quoteFrom;
      return (!!from && qty >= from) || qty > this.cfg.limits.max;
    }

    selectedFees() {
      const map = {
        setup: ['setup'],
        setup_pattern: ['setup', 'pattern'],
        setup_full: ['setup', 'full']
      };
      const keys = map[this.state.tier] || [];
      return keys.map((k) => this.cfg.fees[k]).filter(Boolean);
    }

    selectedRadio() {
      return this.els.radios.find((r) => r.checked) || null;
    }

    /* The color as it should read on the order: "Color 14 (#4AC2D9)" or
       "Other: PMS 2597C". Null when nothing usable is picked, or on the v1
       section, which has no color UI at all. */
    colorValue() {
      if (!this.els.colorRadios.length) return null;
      if (this.state.color === 'other') {
        const text = this.els.colorOther ? this.els.colorOther.value.trim() : '';
        return text ? 'Other: ' + text : null;
      }
      const radio = this.els.colorRadios.find((r) => r.checked);
      return radio ? 'Color ' + radio.value + ' (' + radio.dataset.hex + ')' : null;
    }

    /* ---------- rendering ---------- */

    render() {
      const qty = this.state.qty;
      const quote = this.needsQuote(qty);
      const tier = this.tierForQty(Math.max(qty, this.cfg.limits.min));

      this.els.cards.forEach((card) => {
        const radio = card.querySelector('input');
        card.classList.toggle('is-selected', !!radio && radio.checked);
      });

      /* Stepped flow: steps 2 and 3 stay locked until a tier is picked.
         Step 3 adapts: artwork upload for artwork tiers, design brief otherwise. */
      const picked = this.selectedRadio();
      const needsArt = !picked || picked.dataset.artworkRequired === 'true';
      this.els.step2.classList.toggle('is-locked', !picked);
      this.els.step3.classList.toggle('is-locked', !picked);
      this.els.artField.hidden = !!picked && !needsArt;
      this.els.step3Label.textContent = needsArt ? this.str.stepArt : this.str.stepBrief;
      this.els.step3Label.htmlFor = needsArt ? 'dns-cs-file' : 'dns-cs-notes';
      this.els.notesLabel.textContent = needsArt ? this.str.notesLabel : this.str.briefLabel;
      this.els.notes.placeholder = needsArt ? this.str.notesPlaceholder : this.str.briefPlaceholder;

      if (this.els.qtyTotal) {
        this.els.qtyTotal.textContent = (this.str.qtyTotalLine || '').replace('{qty}', qty);
        this.els.qtyTotal.classList.toggle('is-error', qty < this.cfg.limits.min);
      }

      this.els.summary.hidden = quote;
      this.els.contact.hidden = !quote;
      if (quote) {
        this.renderQuote(Math.max(qty, this.cfg.limits.min));
        return;
      }

      const effectiveQty = Math.max(qty, this.cfg.limits.min);
      const tierIndex = this.cfg.tiers.indexOf(tier);
      this.els.rows.forEach((row, i) => row.classList.toggle('is-active', i === tierIndex));

      const pairsCents = tier ? tier.cents * effectiveQty : 0;
      this.els.pairsLabel.textContent = tier
        ? effectiveQty + ' pairs × ' + this.fmt.format(tier.cents / 100) + '/pair'
        : effectiveQty + ' pairs';
      this.els.pairsTotal.textContent = this.fmt.format(pairsCents / 100);

      const fees = this.selectedFees();
      const setup = fees.find((f) => f === this.cfg.fees.setup) || null;
      const design = fees.find((f) => f !== this.cfg.fees.setup) || null;

      this.els.setupLine.hidden = !setup;
      if (setup) this.els.setupTotal.textContent = this.fmt.format(setup.cents / 100);

      this.els.designLine.hidden = !design;
      if (design) {
        this.els.designLabel.textContent = design.label;
        this.els.designTotal.textContent = this.fmt.format(design.cents / 100);
      }

      this.els.tierNote.hidden = !!this.state.tier;

      const feeCents = fees.reduce((sum, f) => sum + f.cents, 0);
      this.els.total.textContent = this.fmt.format((pairsCents + feeCents) / 100);
    }

    renderFileList() {
      if (!this.els.fileList) return;
      const files = Array.from(this.els.file.files);
      this.els.fileList.textContent = '';
      files.forEach((f) => {
        const li = document.createElement('li');
        li.textContent = f.name;
        this.els.fileList.appendChild(li);
      });
      this.els.fileList.hidden = !files.length;
    }

    /* Fills the quote panel: the live pair count and a mailto pre-loaded with everything
       they have already told us, so the email lands ready to price. Null-guarded because
       the v1 section (dns-custom-socks.liquid) shares this engine without these hooks. */
    renderQuote(qty) {
      /* blank the total so the mobile order bar can't keep showing a stale Grand Total
         next to a panel that no longer offers a cart */
      if (this.els.total) this.els.total.textContent = '';

      if (this.els.quoteQty) {
        const line = this.str.quoteQty || '';
        this.els.quoteQty.textContent = line.replace('{qty}', qty);
        this.els.quoteQty.hidden = !line;
      }

      if (!this.els.quoteLink) return;

      const email = this.cfg.quoteEmail || '';
      if (!email) return;

      const radio = this.selectedRadio();
      const notes = this.els.notes ? this.els.notes.value.trim() : '';
      const subject = (this.str.quoteSubject || 'Custom sock quote - {qty} pairs').replace('{qty}', qty);

      const body = [
        this.split
          ? 'Pairs: ' + qty + ' (' + this.state.qtyAdult + ' adult + ' + this.state.qtyYouth + ' youth)'
          : 'Pairs: ' + qty
      ];
      if (radio) body.push('Starting point: ' + radio.dataset.title);
      const color = this.colorValue();
      if (color) body.push('Color: ' + color);
      if (notes) body.push('', 'About the design:', notes);
      body.push('', 'Anything else you need from us?');

      this.els.quoteLink.href =
        'mailto:' + email +
        '?subject=' + encodeURIComponent(subject) +
        '&body=' + encodeURIComponent(body.join('\n'));
    }

    setStatus(message, isError) {
      this.els.status.textContent = message || '';
      this.els.status.classList.toggle('is-error', !!isError);
    }

    setBusy(busy, label) {
      this.els.atc.disabled = busy;
      this.els.atc.textContent = busy ? label : this.str.atcLabel;
    }

    /* ---------- add to cart ---------- */

    async addToCart() {
      this.setStatus('');

      const radio = this.selectedRadio();
      if (!radio) {
        this.setStatus(this.str.pickTier, true);
        return;
      }

      if (this.state.qty < this.cfg.limits.min || this.state.qty > this.cfg.limits.max) {
        this.setStatus(this.str.qtyRange.replace('{min}', this.cfg.limits.min).replace('{max}', this.cfg.limits.max), true);
        return;
      }

      /* belt and braces — the button lives inside the panel we hide in quote mode, but
         never let a quote-sized order reach the cart */
      if (this.needsQuote(this.state.qty)) {
        this.setStatus(this.str.quoteOnly || this.str.errorGeneric, true);
        return;
      }

      const tier = this.tierForQty(this.state.qty);
      if (!tier) {
        this.setStatus(this.str.errorGeneric, true);
        return;
      }

      /* color is required wherever the color UI exists (the v1 section has none) */
      if (this.els.colorRadios.length) {
        if (!this.state.color) {
          this.setStatus(this.str.colorRequired, true);
          return;
        }
        if (this.state.color === 'other' && !this.els.colorOther.value.trim()) {
          this.setStatus(this.str.colorOtherRequired, true);
          return;
        }
      }

      const files = Array.from(this.els.file.files);
      const notes = this.els.notes.value.trim();
      this.state.notes = notes;
      const needsArt = radio.dataset.artworkRequired === 'true';
      if (needsArt && !files.length) {
        this.setStatus(this.str.artworkRequired, true);
        return;
      }
      if (!needsArt && !notes) {
        this.setStatus(this.str.briefRequired, true);
        return;
      }
      /* belt and braces — the change handler already rejects oversized selections */
      if (files.length > (this.cfg.maxFiles || 1)) {
        this.setStatus((this.str.tooManyFiles || '').replace(/\{n\}/g, this.cfg.maxFiles || 1), true);
        return;
      }

      this.setBusy(true, files.length ? this.str.busyUpload : this.str.busyCart);

      let sockKey = null;
      try {
        /* Request 1 — the sock line (multipart so the artwork file rides along). */
        const fd = new FormData();
        fd.append('id', tier.id);
        fd.append('quantity', this.state.qty);
        fd.append('properties[Service Tier]', radio.dataset.title);
        fd.append('properties[Pairs Ordered]', String(this.state.qty));
        if (this.split) {
          fd.append('properties[Adult Pairs]', String(this.state.qtyAdult));
          fd.append('properties[Youth Pairs]', String(this.state.qtyYouth));
        }
        const colorProp = this.colorValue();
        if (colorProp) fd.append('properties[Sock Color]', colorProp);
        files.forEach((f, i) => {
          fd.append(i === 0 ? 'properties[Artwork]' : 'properties[Artwork ' + (i + 1) + ']', f);
        });
        if (notes) fd.append('properties[Design Notes]', notes);

        const r1 = await fetch(this.root + 'cart/add.js', { method: 'POST', body: fd });
        const d1 = await r1.json();
        if (!r1.ok) {
          this.setStatus(d1.description || this.str.errorGeneric, true);
          this.setBusy(false);
          return;
        }
        const sockItem = Array.isArray(d1.items) ? d1.items[0] : d1;
        sockKey = sockItem && sockItem.key;

        /* Request 2 — the fee lines. */
        const feeItems = this.selectedFees().map((f) => ({
          id: f.id,
          quantity: 1,
          properties: { For: 'Custom Socks: ' + radio.dataset.title }
        }));

        this.setBusy(true, this.str.busyCart);
        let ok = await this.addFees(feeItems);
        if (!ok) ok = await this.addFees(feeItems); /* one retry for network blips */

        if (!ok) {
          await this.rollbackSock(sockKey);
          this.setBusy(false);
          return;
        }

        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch (e) { /* non-fatal */ }
        window.location.href = this.root + 'cart';
      } catch (e) {
        if (sockKey) await this.rollbackSock(sockKey);
        this.setStatus(this.str.errorGeneric, true);
        this.setBusy(false);
      }
    }

    async addFees(items) {
      try {
        const r = await fetch(this.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        return r.ok;
      } catch (e) {
        return false;
      }
    }

    async rollbackSock(key) {
      let rolledBack = false;
      if (key) {
        try {
          const r = await fetch(this.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          rolledBack = r.ok;
        } catch (e) { /* fall through to warning */ }
      }
      if (rolledBack) {
        this.setStatus(this.str.errorFees, true);
      } else {
        this.setStatus(this.str.errorCartCheck + ' ', true);
        const link = document.createElement('a');
        link.href = this.root + 'cart';
        link.textContent = this.str.cartLink;
        this.els.status.appendChild(link);
      }
    }
  }

  if (!customElements.get('dns-custom-socks')) {
    customElements.define('dns-custom-socks', DnsCustomSocks);
  }
})();

/* Scroll-reveal for the builder section, matching the info page's motion.
   Elements hide only under .dns-cs--js, so no-JS users see everything. */
(function () {
  'use strict';

  var root = document.querySelector('.dns-cs');
  if (!root) return;

  var items = Array.prototype.slice.call(root.querySelectorAll('[data-reveal]'));
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced || !('IntersectionObserver' in window)) {
    items.forEach(function (el) { el.classList.add('is-inview'); });
    return;
  }

  root.classList.add('dns-cs--js');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-inview');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

  items.forEach(function (el) { io.observe(el); });
})();
